import '../index.scss';

import { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { requestWithAuth } from '../../services/api';
import { getValue } from '../../utils/general';
import CommonTable from '../../components/CommonTable';
import TableColumnSearchDropdown from '../../components/TableColumnSearchDropdown';
import TopSection from '../../components/TopSection';

interface RequestParams {
  op?: string;
  ordering?: string;
  page_size?: number;
  page?: number;
  q?: string;
}

// let searchQuery = "";

const FormModal = ({ data, onClose }) => {
  return (
    <Modal
      title={'Changes'}
      open
      width={800}
      okText={null}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Back
        </Button>,
      ]}
    >
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </Modal>
  );
};

const ProfileEdits = () => {
  const [listPagination, setListPagination] = useState({
    total: 0,
    pageSize: 50,
    current: 1,
  });
  const { pageSize, current } = listPagination;
  const [profileEditorData, setProfileEditorData] = useState([]);
  const [filteredInfoOpType, setFilteredInfoOpType] = useState([]);
  const [profileDataCount, setProfileDataCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [sortInfo, setSortInfo] = useState({
    column: '',
    order: '',
  });
  const [showFormModal, setShowFormModal] = useState(false);
  const [changesData, setChangesData] = useState(null);

  const paramsRef = useRef<RequestParams>({});
  const sourceRef = useRef<
    { cancel: (message?: string) => void; token: unknown } | undefined
  >(undefined);
  const { column, order } = sortInfo;

  const fetchProfileData = useCallback(() => {
    const newParams = {
      ...paramsRef.current,
      page_size: pageSize,
      page: current,
    };
    // if (searchQuery) newParams.q = searchQuery;

    if (sourceRef.current)
      sourceRef.current.cancel('Operation canceled by the user.');
    const { CancelToken } = axios; // eslint-disable-line import-x/no-named-as-default-member
    const source = CancelToken.source();
    sourceRef.current = source;

    requestWithAuth(
      'vault-person-delta',
      'GET',
      newParams,
      null,
      source.token,
    ).then((response) => {
      // A newer fetch replaced this one; let it manage the state.
      if (sourceRef.current !== source) return;
      if (response && response.results) {
        setProfileEditorData(response.results);
        setProfileDataCount(response.count);
        setListPagination((prevState) => ({
          ...prevState,
          total: response.count,
        }));
      }
      setIsLoading(false);
    });
  }, [pageSize, current]);

  useEffect(() => {
    return () => {
      paramsRef.current = {};
      setSortInfo({
        column: '',
        order: '',
      });
      setFilteredInfoOpType([]);
      //   searchQuery = "";
    };
  }, []);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData, filteredInfoOpType, column, order]);

  const handleSearch = useCallback((selectedKeys, confirm, dataIndex) => {
    const searchData = selectedKeys[0];
    if (searchData !== undefined) paramsRef.current[dataIndex] = searchData;
    confirm();
  }, []);

  const handleReset = useCallback(
    (clearFilters, _confirm, dataIndex) => {
      clearFilters();
      delete paramsRef.current[dataIndex];
      fetchProfileData();
    },
    [fetchProfileData],
  );

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: (props) => (
      <TableColumnSearchDropdown
        {...props}
        dataIndex={dataIndex}
        onSearch={handleSearch}
        onReset={handleReset}
      />
    ),
    filterIcon: () => <SearchOutlined />,
  });

  const handleTableChange = (pagination, filtersArg, sorter) => {
    Object.keys(filtersArg).reduce((_, key) => {
      let value = '';
      if (filtersArg[key] !== null) value = getValue(filtersArg[key]);
      if (key === 'op') {
        if (!value) {
          setFilteredInfoOpType([]);
          delete paramsRef.current.op;
        } else {
          setFilteredInfoOpType(value.split(','));
          paramsRef.current.op = value.split('\n').join(',');
        }
      }
      return null;
    }, {});
    if (sorter && sorter.order && sorter.columnKey) {
      paramsRef.current.ordering =
        sorter.order === 'descend' ? '-' + sorter.columnKey : sorter.columnKey;
      setSortInfo({
        column: sorter.columnKey,
        order: sorter.order,
      });
    } else {
      setSortInfo({
        column: '',
        order: '',
      });
      delete paramsRef.current.ordering;
    }
    // setParams(params);
    // if (
    //   pagination.pageSize === listPagination.pageSize &&
    //   pagination.current === listPagination.current
    // ) {
    //   fetchProfileData();
    // }
    setListPagination({
      ...pagination,
      pageSize: pagination.pageSize,
      current: pagination.current,
    });
  };

  const toggleFormModal = (flag) => {
    setShowFormModal(flag);
  };

  //   const handleSearchQuery = (searchValue) => {
  //     searchQuery = searchValue;
  //     if (searchQuery) fetchProfileData();
  //   };

  const handleFormReset = () => {
    paramsRef.current = {};
    setProfileEditorData([]);
    setProfileDataCount(0);
    setIsLoading(true);
    setSortInfo({
      column: '',
      order: '',
    });
    setListPagination({
      current: 1,
      pageSize: 50,
      total: 0,
    });
    setFilteredInfoOpType([]);
    // searchQuery = "";
    //fetchProfileData();
  };

  const showModal = (record) => {
    if (record && record.changes) {
      setShowFormModal(true);
      setChangesData(record.changes);
    } else {
      message.info('No changes for this profile', 10);
    }
  };

  const columns = [
    {
      title: 'Creation Date',
      width: 120,
      dataIndex: 'created',
      key: 'created',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'created' && sortInfo.order,
      render: (val) => {
        return (
          <div
            style={{ width: '100%', wordBreak: 'break-word', display: 'block' }}
          >
            {val ? dayjs(val).format('MM/DD/YYYY') : ''}
          </div>
        );
      },
    },
    {
      title: 'b2b_id',
      width: 250,
      dataIndex: 'b2b_id',
      key: 'b2b_id',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'b2b_id' && sortInfo.order,
      ...getColumnSearchProps('b2b_id'),
      render: (_val, rec) => {
        return (
          <div style={{ width: '100%', hyphens: 'none' }}>{rec.b2b_id}</div>
        );
      },
    },
    {
      title: 'b2c_id',
      width: 150,
      dataIndex: 'b2c_id',
      key: 'b2c_id',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'b2c_id' && sortInfo.order,
      ...getColumnSearchProps('b2c_id'),
      render: (_val, rec) => {
        return (
          <div style={{ width: '100%', hyphens: 'none' }}>{rec.b2c_id}</div>
        );
      },
    },
    {
      title: 'Full Name',
      width: 150,
      dataIndex: 'full_name',
      key: 'full_name',
      render: (_val, rec) => {
        return (
          <div style={{ width: '100%', wordBreak: 'break-word' }}>
            {rec.full_name}
          </div>
        );
      },
    },
    {
      title: 'Operations',
      width: 150,
      dataIndex: 'op',
      key: 'op',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'op' && sortInfo.order,
      filters: [
        { text: 'DELETE', value: 'delete' },
        { text: 'INSERT', value: 'insert' },
        { text: 'UPDATE', value: 'update' },
      ],
      filterMultiple: false,
      filteredValue: filteredInfoOpType || [],
      render: (_val, rec) => {
        return (
          <div
            style={{
              width: '100%',
              wordBreak: 'break-word',
              textTransform: 'uppercase',
            }}
          >
            {rec.op}
          </div>
        );
      },
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 160,
      render: (val) => {
        return <div className="column-style">{val.name}</div>;
      },
    },
  ];

  return (
    <>
      <div className="people-editor">
        <TopSection
          count={profileDataCount}
          placeholder="Search names, companies, LinkedIn, IDs, phones, or emails"
          //   handleSearchQuery={handleSearchQuery}
          handleFormReset={handleFormReset}
          toggleCreateForm={toggleFormModal}
          //   showSearch
          showNewButton={false}
        />
        <Spin size="default" spinning={isLoading}>
          <CommonTable
            columns={columns}
            data={profileEditorData}
            rowClassName={() => 'editable-row'}
            onTableChange={handleTableChange}
            pagination={{ ...listPagination }}
            pageSize={listPagination.pageSize}
            loading={isLoading}
            rowEventHandlers={showModal}
          />
        </Spin>
      </div>
      {showFormModal && (
        <FormModal
          data={changesData}
          onClose={() => {
            setShowFormModal(false);
          }}
        />
      )}
    </>
  );
};

export default ProfileEdits;
